import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { getPhoneNumbers } from '@ecp/common/src/utils/textUtil';
import useEventCallback from '@ecp/common/src/hooks/useEventCallback';
import moTheme from '@ecp/common/src/style/theme/mo';
import { FlexBoxColumn, HorizontalDivider, FlexBox } from '@ecp/common/src/layouts/flex/styled';
import { TextBox } from '@ecp/common/src/text/TextBox';
import { InnerButton } from '@ecp/common/src/components/button/mo/Button';
import Input from '@ecp/common/src/components/input/mo/Input';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import TimerAdornment from '@mo-components/common/TimerAdornment';
import { useAlertDialog } from '@mo-hooks/common/useModal';
import useValidate from '@mo-hooks/common/useValidate';
import authApi from '@mo-apis/common/authApi';
import FullPagePopup from '@ecp/common/src/components/modal/mo/popup/FullPagePopup';
import PhoneNumberInput from '@mo-components/member/input/PhoneNumberInput';
import useTimer from '@mo-components/common/useTimer';
import { VERIFY_TYPE, SEND_VIA } from '@mo-const/member/memberConst';
import { isEmpty } from 'lodash-es';

import FindInputForm from '@mo-pages/member/login/popup/FindInputForm'

const initForm = {
  certKey: '',
  cellphoneNumber: '',
  email: '',
};
const OtpVerifyInputPopup = ({ userOtpInfo, open, onClose, onConfirm, verifyType, authType }) => {
  const { showAlertDialog } = useAlertDialog();
  const { startTimer, isRunning, minutes, seconds, leftTime } = useTimer({ time: authType === SEND_VIA.EMAIL ? 300 : 180 });

  const [isSend, setIsSend] = useState(false);
  const [form, setForm] = useState({ ...initForm });
  const [error, setError] = useState({ ...initForm });

  const { formValidate } = useValidate();
  const formValidators = useMemo(
    () => ({
      cellphoneNumber: [
        {
          when: ['created'],
          message: '휴대전화번호 정보가 없습니다.',
          validator: ({ cellphoneNumber }) => !isEmpty(cellphoneNumber)
        }
      ],
      email: [
        {
          when: ['created'],
          message: '이메일 정보가 없습니다.',
          validator: ({ email }) => !isEmpty(email)
        },
      ],
    }),
    [form.cellphoneNumber, form.email]
  );

  const certInputRef = useRef();

  useEffect(() => {
    setForm({
      ...initForm,
      userId: userOtpInfo?.userId,
      siteNumber: userOtpInfo?.siteNumber,
      cellphoneNumber: userOtpInfo?.cellphoneNumber,
      email: userOtpInfo?.email,
    })
    setIsSend(false);
  }, [open]);

  const handlePopupConfirm = useCallback(async () => {
    if (!isSend) {
      showAlertDialog('인증요청 버튼을 눌러 휴대전화번호인증을 해주세요');
      return;
    } else if (!isRunning) {
      showAlertDialog('인증번호 입력시간이 초과되었습니다.\n인증번호를 재전송해주세요');
      return;
    } else if (form.certKey === '') {
      showAlertDialog('인증번호를 입력해주세요');
      return;
    }

    onConfirm && onConfirm(form);
  }, [form, isRunning, isSend, onConfirm, showAlertDialog]);

  const handleSendButtonClick = useCallback(async () => {
    if (!(await formValidate('created', form, setError, formValidators))) {
      return;
    }

    if (isSend && leftTime < 2) {
      return;
    }

    const [cellphoneNumber1, cellphoneNumber2, cellphoneNumber3] = getPhoneNumbers(userOtpInfo.cellphoneNumber);
    const email = userOtpInfo.email;

    const { result, message } = authType === SEND_VIA.EMAIL ?
      await authApi.sendKeyViaEmail({
        userId: userOtpInfo.userId,
        siteNumber: userOtpInfo.siteNumber,
        email: email,
        accessToken: userOtpInfo.otpAccessToken ? `Bearer ${userOtpInfo.otpAccessToken}` : undefined,
      })
      : await authApi.sendCellphoneKeyViaSMS({
        userId: userOtpInfo.userId,
        siteNumber: userOtpInfo.siteNumber,
        cellphoneNumber1,
        cellphoneNumber2,
        cellphoneNumber3,
        accessToken: userOtpInfo.otpAccessToken ? `Bearer ${userOtpInfo.otpAccessToken}` : undefined,
      });

    if (
      result &&
      (process.env.REACT_APP_ENV === 'local' ||
        process.env.REACT_APP_ENV === 'dev' ||
        process.env.REACT_APP_ENV === 'stg' ||
        process.env.REACT_APP_ENV === 'qa-local')
    ) {
      //FIXME. 개발용 화면임
      showAlertDialog(`${result}`, () => {
        certInputRef?.current?.focus();
      });
    } else if (result !== '') {
      showAlertDialog(message ?? '인증번호 발송에 실패했습니다.');
      return;
    }
    setIsSend(true);
    setForm((prev) => ({ ...prev, certKey: '' }));
    startTimer();
  }, [
    formValidate,
    form,
    formValidators,
    isSend,
    leftTime,
    userOtpInfo?.cellphoneNumber,
    userOtpInfo?.userId,
    startTimer,
    showAlertDialog,
  ]);

  useEffect(() => {
    if (isSend && form.certKey === '') {
      certInputRef?.current?.focus();
    }
  }, [isSend, form]);

  const handleInputChange = useEventCallback(
    (value, { key }) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [setForm]
  );

  return (
    <FullPagePopup
      open={open}
      header={'2단계 인증'}
      onClose={onClose}
      cancelButtonText={'닫기'}
      onCancelClick={onClose}
      onConfirmClick={handlePopupConfirm}
    >
      <FlexBoxColumn width={'100%'} alignItems={'flex-start'}>
        <TextBox width={'100%'} size={moTheme.font.size.$13} weight={moTheme.font.weight.medium2}>
          {verifyType == VERIFY_TYPE.LOGIN_USER && (
            <>
              고객님의 안전한 계정 관리를 위해 2단계 인증을 진행합니다.<br />
              등록된 휴대전화 번호로 인증해 주세요.
            </>
          )}
          {verifyType == VERIFY_TYPE.VERIFY_USER && (
            <>
              로그인 2단계 인증이 설정되어 있습니다.<br />
              회원 정보에 등록된 휴대전화 번호로 인증해 주세요.
            </>
          )}
        </TextBox>
        <Spacing top={20} />
        <HorizontalDivider color="#141415" />
        <Spacing top={20} />
        <FlexBoxColumn style={{ width: '100%', alignItems: 'flex-start', gap: '6px' }}>
          {authType === SEND_VIA.SMS && (
            <>
              <InputLabel>휴대전화번호</InputLabel>
              <FlexBox style={{ width: '100%', alignItems: 'flex-start', gap: '6px' }}>
                <PhoneNumberInput
                  disabled={'ture'}
                  minWidth={'320px'}
                  value={userOtpInfo.cellphoneNumber}
                  onChange={handleInputChange}
                  dataKey={'cellphoneNumber'}
                  error={error.cellphoneNumber}
                  errorMessage={error.cellphoneNumber}
                  showSeparator
                />
                <InnerButton type={'Default'} width={'80px'} height={'36px'} onClick={handleSendButtonClick}>
                  {isSend ? '재전송' : '인증요청'}
                </InnerButton>
              </FlexBox>
            </>
          )}
          {authType === SEND_VIA.EMAIL && (
            <>
              <InputLabel>이메일</InputLabel>
              <FlexBox style={{ width: '100%', alignItems: 'flex-start', gap: '6px' }}>
                <Input
                  disabled={'ture'}
                  id={'user_email'}
                  minWidth={'230px'}
                  type={'text'}
                  value={userOtpInfo.email}
                  data-key={'email'}
                  error={error.email}
                  errorMessage={error.email}
                />
                <InnerButton type={'Default'} width={'80px'} height={'36px'} onClick={handleSendButtonClick}>
                  {isSend ? '재전송' : '인증요청'}
                </InnerButton>
              </FlexBox>
            </>
          )}
          <Spacing top={20} />
          {isSend && (
            <FindInputForm labelText="인증번호">
              <Input
                disabled={!isRunning}
                minWidth={'320px'}
                value={form.certKey}
                onlyNumber
                onEnter={handlePopupConfirm}
                endAdornment={<TimerAdornment minutes={minutes} seconds={seconds} />}
                onChange={handleInputChange}
                data-key={'certKey'}
                ref={certInputRef}
              />
            </FindInputForm>
          )}
          {isSend && !isRunning && (
            <TextBox style={{ fontSize: moTheme.font.size.$10, color: moTheme.color.error1 }}>
              인증번호 입력시간이 초과되었습니다. 인증번호를 재전송해주세요.
            </TextBox>
          )}
        </FlexBoxColumn>
      </FlexBoxColumn>
    </FullPagePopup>
  );
};

export default OtpVerifyInputPopup;

const InputLabel = styled.label`
  min-width: 100px;
  font-size: 15px;
  font-weight: 500;
  padding-bottom: 6px;
`;

OtpVerifyInputPopup.defaultProps = {
  userOtpInfo: {},
  open: false,
  time: 100,
};

OtpVerifyInputPopup.propTypes = {
  userOtpInfo: PropTypes.object,
  open: PropTypes.bool,
  update: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  time: PropTypes.number,
  verifyType: PropTypes.string,
  authType: PropTypes.string,
};
